import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllTimers,
  getTimerStats,
  updateTimer,
  deleteTimer,
  subscribeToAllTimers
} from '../services/vpsApi';
import { getAllCompanies } from '../services/vpsCompanies';
import TimerControls from '../components/TimerControls';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';

// Default sound settings
const DEFAULT_SOUND_SETTINGS = {
  useCompanySettings: true,
  enabled: true,
  volume: 60,
  beepCount: 3,
  pitch: 'medium',
  customSoundUrl: ''
};

function Dashboard() {
  const [timers, setTimers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [controlsTimer, setControlsTimer] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [soundSettingsModal, setSoundSettingsModal] = useState(null); // Timer being edited
  const [soundFormData, setSoundFormData] = useState({ ...DEFAULT_SOUND_SETTINGS });
  const [uploadingSound, setUploadingSound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Subscribe to real-time updates via VPS WebSocket
    const unsubscribe = subscribeToAllTimers((updatedTimers) => {
      setTimers(updatedTimers);
      updateStats();
      setLoading(false);
    });

    // Load companies for sound settings dropdown
    const loadCompanies = async () => {
      try {
        const companiesData = await getAllCompanies();
        setCompanies(companiesData);
      } catch (err) {
        console.error('Error loading companies:', err);
      }
    };
    loadCompanies();

    return () => unsubscribe();
  }, []);

  // Real-time updates via VPS WebSocket + Apps Script Webhook
  // Manual sync triggers VPS API to pull from Google Sheets

  const updateStats = async () => {
    try {
      const statsData = await getTimerStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error updating stats:', err);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await fetch(`${API_URL}/webhook/sheets-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ manual: true })
      });

      const result = await response.json();

      if (result.success) {
        setLastSyncTime(new Date());
        alert(`同步完成！已同步 ${result.recordsSynced} 筆資料\n資料會自動即時更新`);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      alert('同步失敗：' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  // Auto-sync handled by VPS WebSocket + Apps Script Webhook

  const formatLastSyncTime = () => {
    if (!lastSyncTime) return '尚未同步';
    const now = new Date();
    const diff = Math.floor((now - lastSyncTime) / 1000);
    if (diff < 60) return `${diff} 秒前`;
    if (diff < 3600) return `${Math.floor(diff / 60)} 分鐘前`;
    return lastSyncTime.toLocaleTimeString('zh-TW');
  };

  const handleStartTimer = async (id) => {
    try {
      // Get current timer data to check if it was paused
      const timer = timers.find(t => t.id === id);

      if (timer?.status === 'paused' && timer?.startTime && timer?.pausedElapsed !== undefined) {
        // Resume from pause - keep original startTime and calculate new startTime based on pausedElapsed
        const now = new Date();
        const newStartTime = new Date(now - timer.pausedElapsed * 1000).toISOString();

        await updateTimer(id, {
          status: 'active',
          startTime: newStartTime,
          pausedElapsed: undefined // Clear pausedElapsed when resuming
        });
      } else {
        // First time start - set new startTime
        await updateTimer(id, {
          status: 'active',
          startTime: new Date().toISOString()
        });
      }
    } catch (err) {
      alert('啟動計時器失敗：' + err.message);
    }
  };

  const handlePauseTimer = async (id) => {
    try {
      // Get current timer to calculate elapsed time
      const timer = timers.find(t => t.id === id);

      if (timer?.startTime) {
        const startTime = new Date(timer.startTime);
        const now = new Date();
        const elapsedSeconds = Math.floor((now - startTime) / 1000);

        await updateTimer(id, {
          status: 'paused',
          pausedElapsed: elapsedSeconds // Save current elapsed time
        });
      } else {
        // No startTime, just pause
        await updateTimer(id, {
          status: 'paused'
        });
      }
    } catch (err) {
      alert('暫停計時器失敗：' + err.message);
    }
  };

  const handleStopTimer = async (id) => {
    if (!window.confirm('確定要停止這個計時器嗎？停止後將記錄結束時間。')) return;

    try {
      await updateTimer(id, {
        status: 'stopped',
        endTime: new Date().toISOString()
      });
      alert('計時器已停止並記錄結束時間');
    } catch (err) {
      alert('停止計時器失敗：' + err.message);
    }
  };

  const handleResetTimer = async (id) => {
    if (!window.confirm('確定要重置這個計時器嗎？這將清除所有計時記錄，回到初始狀態。')) return;

    try {
      await updateTimer(id, {
        status: 'pending',
        startTime: null,
        endTime: null,
        currentSegmentIndex: 0,
        bubbleTime: {
          active: false
        },
        currentStage: null
      });
      alert('計時器已重置，可以重新啟動');
    } catch (err) {
      alert('重置計時器失敗：' + err.message);
    }
  };

  const handleDeleteTimer = async (id) => {
    if (!window.confirm('確定要刪除這個計時器嗎？')) return;

    try {
      await deleteTimer(id);
    } catch (err) {
      alert('刪除計時器失敗：' + err.message);
    }
  };

  const handleUpdateRemainingPlayers = async (id, currentValue) => {
    const newValue = prompt('請輸入剩餘人數：', currentValue || '');

    if (newValue === null || newValue === '') return;

    const players = parseInt(newValue);
    if (isNaN(players) || players < 0) {
      alert('請輸入有效的數字');
      return;
    }

    try {
      await updateTimer(id, {
        remainingPlayers: players.toString()
      });
      alert('剩餘人數已更新');
    } catch (err) {
      alert('更新失敗：' + err.message);
    }
  };

  const handleUpdateTotalChips = async (id, currentValue) => {
    const newValue = prompt('請輸入總碼量：', currentValue || '');

    if (newValue === null || newValue === '') return;

    try {
      await updateTimer(id, {
        totalChips: newValue
      });
      alert('總碼量已更新');
    } catch (err) {
      alert('更新失敗：' + err.message);
    }
  };

  const handleUpdateMarquee = async (id, currentValue) => {
    const newValue = prompt('請輸入跑馬燈內容：', currentValue || '');

    if (newValue === null) return;

    try {
      await updateTimer(id, {
        marqueeContent: newValue
      });
      alert('跑馬燈內容已更新');
    } catch (err) {
      alert('更新失敗：' + err.message);
    }
  };

  const handleUpdateBackground = async (id, currentValue) => {
    const newValue = prompt('請輸入背景圖片 URL：', currentValue || '');

    if (newValue === null) return;

    try {
      await updateTimer(id, {
        backgroundImage: newValue
      });
      alert('背景圖片已更新');
    } catch (err) {
      alert('更新失敗：' + err.message);
    }
  };

  const handleGetLink = (id, type) => {
    const baseUrl = window.location.origin;
    let link, linkName;

    if (type === 'control') {
      link = `${baseUrl}/control/${id}`;
      linkName = '控制台';
    } else if (type === 'full') {
      link = `${baseUrl}/timer/${id}/full`;
      linkName = '完整版';
    } else if (type === 'full2') {
      link = `${baseUrl}/timer/${id}/full2`;
      linkName = '完整版二';
    } else {
      link = `${baseUrl}/timer/${id}`;
      linkName = '簡易版';
    }

    navigator.clipboard.writeText(link);
    alert(`已複製${linkName}連結到剪貼簿！\n${link}`);
  };

  // Open sound settings modal
  const handleOpenSoundSettings = (timer) => {
    setSoundSettingsModal(timer);
    setSoundFormData({
      ...DEFAULT_SOUND_SETTINGS,
      ...(timer.soundSettings || {}),
      companyId: timer.companyId || ''
    });
  };

  // Save sound settings
  const handleSaveSoundSettings = async () => {
    try {
      await updateTimer(soundSettingsModal.id, {
        soundSettings: {
          useCompanySettings: soundFormData.useCompanySettings,
          enabled: soundFormData.enabled,
          volume: soundFormData.volume,
          beepCount: soundFormData.beepCount,
          pitch: soundFormData.pitch,
          customSoundUrl: soundFormData.customSoundUrl
        },
        companyId: soundFormData.companyId || null
      });
      alert('音效設定已儲存');
      setSoundSettingsModal(null);
    } catch (err) {
      alert('儲存失敗：' + err.message);
    }
  };

  // Update sound form data helper
  const updateSoundForm = (key, value) => {
    setSoundFormData(prev => ({ ...prev, [key]: value }));
  };

  // Handle audio file upload for timer
  const handleTimerAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/x-m4a', 'audio/mp4'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|webm|m4a)$/i)) {
      alert('請上傳音檔格式 (MP3, WAV, OGG, WebM, M4A)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('檔案大小不能超過 5MB');
      return;
    }

    try {
      setUploadingSound(true);
      const formDataUpload = new FormData();
      formDataUpload.append('audio', file);

      const response = await fetch(`${API_URL}/uploads/sound`, {
        method: 'POST',
        body: formDataUpload
      });

      const result = await response.json();

      if (result.success) {
        updateSoundForm('customSoundUrl', result.url);
        alert('音檔上傳成功！');
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('上傳失敗：' + err.message);
    } finally {
      setUploadingSound(false);
    }
  };

  // Play preview sound for timer
  const playTimerPreviewSound = () => {
    const url = soundFormData.customSoundUrl;
    if (url) {
      const audio = new Audio(url);
      audio.volume = soundFormData.volume / 100;
      audio.play().catch(err => {
        alert('無法播放音檔：' + err.message);
      });
    }
  };

  if (loading) {
    return <div className="loading">載入中...</div>;
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1 className="page-title">Multi-Counter 儀表板</h1>
          <p className="page-subtitle">資料分析與計時器管理系統 (VPS 版)</p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-secondary"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? '同步中...' : '手動同步'}
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/companies')}>
            公司管理
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/devices')}>
            設備管理
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/data-calculation')}>
            德州撲克計算器
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/slideshows')}>
            輪播管理
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/animations')}>
            動畫管理
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/languages')}>
            語言管理
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/admin')}>
            後台管理
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/analytics')} style={{backgroundColor: '#9c27b0'}}>
            📊 統計分析
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/settings')} style={{backgroundColor: '#607d8b'}}>
            ⚙️ 全域設定
          </button>
        </div>
      </header>

      <div className="sync-status">
        <div className={`sync-indicator ${syncing ? 'syncing' : ''}`}>
          🔄 即時更新已啟用 (VPS WebSocket + Apps Script Webhook)
        </div>
        <div className="last-sync">
          上次同步：{formatLastSyncTime()} |
          <a href="https://multi-counter.bancheng.tech/api/webhook/test" target="_blank" rel="noopener noreferrer" style={{marginLeft: '10px', color: '#4CAF50'}}>
            測試 Webhook
          </a>
        </div>
      </div>

      {error && <div className="error">錯誤：{error}</div>}

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">總計時器</div>
          </div>
          <div className="stat-card active">
            <div className="stat-value">{stats.active}</div>
            <div className="stat-label">進行中</div>
          </div>
          <div className="stat-card paused">
            <div className="stat-value">{stats.paused}</div>
            <div className="stat-label">已暫停</div>
          </div>
          <div className="stat-card completed">
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">已完成</div>
          </div>
        </div>
      )}

      <div className="timers-section">
        <h2 className="section-title">所有計時器</h2>
        {timers.length === 0 ? (
          <div className="empty-state">
            <p>尚無計時器</p>
            <p>請先同步 Google Sheets 或前往後台新增</p>
          </div>
        ) : (
          <div className="timers-grid">
            {timers.map((timer) => (
              <div key={timer.id} className="timer-card">
                <div className="timer-header">
                  <h3 className="timer-name">{timer.name}</h3>
                  <span className={`status-badge status-${timer.status}`}>
                    {timer.status}
                  </span>
                </div>

                <div className="timer-info">
                  {timer.structureName && (
                    <div className="info-item">
                      <span className="label">賽事結構：</span>
                      <span className="value">{timer.structureName}</span>
                    </div>
                  )}
                  {timer.totalGroups && (
                    <div className="info-item">
                      <span className="label">總組數：</span>
                      <span className="value">{timer.totalGroups}</span>
                    </div>
                  )}
                  {timer.currentStage && (
                    <div className="info-item">
                      <span className="label">目前階段：</span>
                      <span className="value">{timer.currentStage}</span>
                    </div>
                  )}
                  {timer.remainingPlayers && (
                    <div className="info-item">
                      <span className="label">剩餘人數：</span>
                      <span className="value">
                        {timer.remainingPlayers}
                        <button
                          className="btn-icon"
                          onClick={() => handleUpdateRemainingPlayers(timer.id, timer.remainingPlayers)}
                          title="更新剩餘人數"
                        >
                          ✏️
                        </button>
                      </span>
                    </div>
                  )}
                  {timer.prizePool && (
                    <div className="info-item">
                      <span className="label">獎金區段：</span>
                      <span className="value">{timer.prizePool.length} 個</span>
                    </div>
                  )}
                </div>

                <div className="timer-actions">
                  {timer.status === 'pending' && (
                    <button className="btn btn-success btn-sm" onClick={() => handleStartTimer(timer.id)}>
                      開始
                    </button>
                  )}
                  {timer.status === 'active' && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handlePauseTimer(timer.id)}>
                      暫停
                    </button>
                  )}
                  {timer.status === 'paused' && (
                    <>
                      <button className="btn btn-success btn-sm" onClick={() => handleStartTimer(timer.id)}>
                        繼續
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleStopTimer(timer.id)}>
                        停止
                      </button>
                    </>
                  )}
                  {(timer.status === 'stopped' || timer.status === 'completed') && (
                    <button className="btn btn-info btn-sm" onClick={() => handleResetTimer(timer.id)}>
                      🔄 重置
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={() => window.open(`/timer/${timer.id}`, '_blank')}>
                    📱 簡易版
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => window.open(`/timer/${timer.id}/full`, '_blank')}>
                    🖥️ 完整版
                  </button>
                  <button className="btn btn-info btn-sm" onClick={() => window.open(`/timer/${timer.id}/full2`, '_blank')}>
                    🖥️ 完整版二
                  </button>
                  <button className="btn btn-success btn-sm" onClick={() => window.open(`/control/${timer.id}`, '_blank')}>
                    ⚙️ 控制台
                  </button>
                  {timer.refreshLink && (
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={async () => {
                        try {
                          const response = await fetch(timer.refreshLink);
                          if (response.ok) {
                            alert('✅ 已發送刷新信號至所有顯示頁面');
                          } else {
                            alert('❌ 刷新失敗');
                          }
                        } catch (error) {
                          console.error('刷新錯誤:', error);
                          alert('❌ 刷新失敗：' + error.message);
                        }
                      }}
                      title="強制刷新所有顯示此計時器的頁面"
                    >
                      🔄 強制刷新
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => handleUpdateMarquee(timer.id, timer.marqueeContent)}>
                    編輯跑馬燈
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleUpdateBackground(timer.id, timer.backgroundImage)}>
                    設定背景
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleOpenSoundSettings(timer)}>
                    🔊 音效設定
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTimer(timer.id)}>
                    刪除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {controlsTimer && (
        <TimerControls
          timer={controlsTimer}
          onClose={() => setControlsTimer(null)}
        />
      )}

      {/* Sound Settings Modal */}
      {soundSettingsModal && (
        <div className="modal-overlay" onClick={() => setSoundSettingsModal(null)}>
          <div className="modal-content sound-settings-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🔊 音效設定 - {soundSettingsModal.name}</h2>

            <div className="form-group">
              <label>關聯公司</label>
              <select
                value={soundFormData.companyId || ''}
                onChange={(e) => updateSoundForm('companyId', e.target.value)}
              >
                <option value="">無（使用預設設定）</option>
                {companies.map(company => (
                  <option key={company._id} value={company._id}>
                    {company.名稱}
                  </option>
                ))}
              </select>
              <small className="help-text">選擇公司後可繼承該公司的音效設定</small>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={soundFormData.useCompanySettings}
                  onChange={(e) => updateSoundForm('useCompanySettings', e.target.checked)}
                />
                使用公司設定
              </label>
              <small className="help-text">勾選後將使用關聯公司的音效設定</small>
            </div>

            {!soundFormData.useCompanySettings && (
              <>
                <div className="form-section">
                  <h3>獨立音效設定</h3>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={soundFormData.enabled}
                        onChange={(e) => updateSoundForm('enabled', e.target.checked)}
                      />
                      啟用跳階段音效
                    </label>
                  </div>

                  {soundFormData.enabled && (
                    <>
                      <div className="form-group">
                        <label>音量 ({soundFormData.volume}%)</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={soundFormData.volume}
                          onChange={(e) => updateSoundForm('volume', parseInt(e.target.value))}
                          className="volume-slider"
                        />
                      </div>

                      <div className="form-group">
                        <label>嗶聲次數</label>
                        <select
                          value={soundFormData.beepCount}
                          onChange={(e) => updateSoundForm('beepCount', parseInt(e.target.value))}
                        >
                          <option value={1}>1 次</option>
                          <option value={2}>2 次</option>
                          <option value={3}>3 次</option>
                          <option value={4}>4 次</option>
                          <option value={5}>5 次</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>音調</label>
                        <select
                          value={soundFormData.pitch}
                          onChange={(e) => updateSoundForm('pitch', e.target.value)}
                        >
                          <option value="low">低音 (440Hz)</option>
                          <option value="medium">中音 (880Hz)</option>
                          <option value="high">高音 (1320Hz)</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>自訂音檔</label>
                        <div className="sound-upload-section">
                          <div className="upload-row">
                            <input
                              type="file"
                              accept="audio/*"
                              onChange={handleTimerAudioUpload}
                              disabled={uploadingSound}
                              style={{ display: 'none' }}
                              id="audio-upload-timer"
                            />
                            <label htmlFor="audio-upload-timer" className="btn btn-secondary upload-btn">
                              {uploadingSound ? '上傳中...' : '📁 選擇音檔'}
                            </label>
                            {soundFormData.customSoundUrl && (
                              <>
                                <button type="button" className="btn btn-info preview-btn" onClick={playTimerPreviewSound}>
                                  🔊 試聽
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-danger clear-btn"
                                  onClick={() => updateSoundForm('customSoundUrl', '')}
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                          {soundFormData.customSoundUrl && (
                            <div className="current-sound">
                              <small>目前音檔：{soundFormData.customSoundUrl.split('/').pop()}</small>
                            </div>
                          )}
                          <div className="url-input-row">
                            <input
                              type="url"
                              value={soundFormData.customSoundUrl}
                              onChange={(e) => updateSoundForm('customSoundUrl', e.target.value)}
                              placeholder="或直接輸入音檔 URL"
                            />
                          </div>
                          <small className="help-text">支援 MP3, WAV, OGG, M4A 格式，最大 5MB。留空使用預設嗶聲</small>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setSoundSettingsModal(null)}>
                取消
              </button>
              <button className="btn btn-primary" onClick={handleSaveSoundSettings}>
                儲存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
